import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "../components/layout/AppLayout";
import { Card } from "../components/ui/Card";
import { api } from "../lib/api";

type InterviewItem = {
  id: string;
  title: string;
  status: "in_progress" | "completed" | "abandoned";
  candidate_name: string;
  candidate_avatar: string | null;
  created_at: string;
};

const STATUS_LABEL: Record<InterviewItem["status"], string> = {
  in_progress: "In Progress",
  completed: "Completed",
  abandoned: "Abandoned",
};

export default function CompanyInterviews() {
  const [items, setItems] = useState<InterviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ items: InterviewItem[] }>("/api/company/interviews")
      .then((res) => setItems(res.items))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load interviews."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppLayout title="AI Interview">
      <p className="text-sm font-semibold text-navy">AI Online Chat Interview & Report</p>
      <p className="mt-1 text-xs text-slate-500">
        Status and reports will appear here when candidates respond to invitations from Team-fit Matchmaker.
      </p>

      {error && <Card className="mt-3 border border-coral/30 bg-coral/5 text-xs text-coral">{error}</Card>}
      {loading && <p className="mt-3 text-xs text-slate-400">Loading interviews...</p>}

      {!loading && items.length === 0 && (
        <Card className="mt-3 border border-dashed border-slate-200 text-center text-xs text-slate-400">
          No interviews conducted yet.
        </Card>
      )}

      <div className="mt-3 space-y-2">
        {items.map((iv) => (
          <Card key={iv.id} className="border border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 flex-shrink-0 rounded-full bg-gradient-to-br from-teal to-coral" />
                <div>
                  <p className="text-sm font-semibold text-navy">{iv.candidate_name}</p>
                  <p className="text-[11px] text-slate-400">{new Date(iv.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</p>
                </div>
              </div>
              <span className="rounded-full bg-slate-50 px-2 py-1 text-[10px] text-slate-500">
                {STATUS_LABEL[iv.status]}
              </span>
            </div>
            {iv.status === "completed" && (
              <Link
                to={`/company/interview/${iv.id}/report`}
                className="mt-2 inline-block text-xs font-medium text-teal-dark"
              >
                View Company Report →
              </Link>
            )}
          </Card>
        ))}
      </div>
    </AppLayout>
  );
}
